import { useState, useEffect } from 'react'
import {
  FiPlus, FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiTrash2,
  FiUpload, FiAlertCircle, FiPaperclip
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import SolicitudAusenciaModal from '../../components/v2/SolicitudAusenciaModal'
import { API_URL } from '../../config/api'
import { PageHeader, Stat, Card, Btn, Empty, Pill, Tabs } from '../../ui'
import { formatearFecha } from '../../utils/fechas'
import MiCalendarioMes from '../../components/v2/MiCalendarioMes'

/*
 * MisAusencias (V2) — versión rediseñada con DS v2.
 * Misma lógica que el original: cargar ausencias + resumen, filtros, cancelar, adjuntar doc.
 * Cambian:
 *   - PageHeader v2 con eyebrow numerada y CTA "Nueva solicitud"
 *   - Stat cards en lugar de cards con gradient
 *   - FilterSelect en pill v2 para año y estado
 *   - Card primitive con tabla rediseñada (Pill para tipo y estado)
 *   - Empty state primitive
 */

const TIPO_LABEL = {
  vacaciones: 'Vacaciones',
  baja_laboral: 'Baja laboral',
  baja_medica: 'Baja médica',
  dia_libre: 'Día libre',
  permiso_retribuido: 'Permiso retribuido',
  asuntos_propios: 'Asuntos propios',
  ausencia_justificada: 'Ausencia justificada',
  otro: 'Otro'
}

const TIPO_TONE = {
  vacaciones: 'brand',
  baja_laboral: 'red',
  baja_medica: 'red',
  dia_libre: 'emerald',
  permiso_retribuido: 'purple',
  asuntos_propios: 'emerald',
  ausencia_justificada: 'amber',
  otro: 'neutral'
}

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  documentacion_requerida: 'Doc. solicitada',
  cancelada: 'Cancelada'
}

const ESTADO_TONE = {
  pendiente: 'amber',
  aprobada: 'emerald',
  rechazada: 'red',
  documentacion_requerida: 'amber',
  cancelada: 'neutral'
}

const EXT_PERMITIDAS = ['pdf', 'jpg', 'jpeg', 'png']

// Control de subida de justificante con drag & drop (mismo patrón que B3 en
// SolicitudAusenciaModal). Valida extensión y delega el archivo en onArchivo.
function SubidaJustificante({ ausenciaId, subiendo, onArchivo, texto = 'Adjuntar' }) {
  const [arrastrando, setArrastrando] = useState(false)

  const validarYEnviar = (file) => {
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!EXT_PERMITIDAS.includes(ext)) {
      toast.error('Formato no válido. Adjunta un PDF, JPG o PNG.')
      return
    }
    onArchivo(ausenciaId, file)
  }

  return (
    <label
      onDragOver={e => { e.preventDefault(); setArrastrando(true) }}
      onDragLeave={e => { e.preventDefault(); setArrastrando(false) }}
      onDrop={e => { e.preventDefault(); setArrastrando(false); validarYEnviar(e.dataTransfer.files?.[0]) }}
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-dashed cursor-pointer transition-colors ${arrastrando ? 'border-[var(--ink-3)] bg-[var(--bg-2)]' : 'border-[var(--line)] hover:border-[var(--ink-3)] hover:bg-[var(--bg-2)]'} ${subiendo ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <FiUpload size={11} />
      {subiendo ? 'Subiendo…' : (arrastrando ? 'Suelta aquí' : texto)}
      <input
        type="file"
        className="sr-only"
        accept=".pdf,.jpg,.jpeg,.png"
        disabled={subiendo}
        onChange={e => validarYEnviar(e.target.files[0])}
      />
    </label>
  )
}

function FilterSelect({ icon: Icon, label, value, onChange, children }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
      {Icon && <Icon size={13} className="text-[var(--ink-3)] flex-shrink-0" />}
      <label className="text-[var(--ink-3)] whitespace-nowrap">{label}</label>
      <select
        value={value}
        onChange={onChange}
        className="bg-transparent outline-none text-[var(--ink)] font-medium cursor-pointer"
      >
        {children}
      </select>
    </div>
  )
}

const MisAusenciasV2 = () => {
  const [ausencias, setAusencias] = useState([])
  const [loading, setLoading] = useState(true)
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear())
  const [filtroEstado, setFiltroEstado] = useState('todas')
  const [modalAusencia, setModalAusencia] = useState({ open: false, ausencia: null })
  const [resumen, setResumen] = useState([])
  const [subiendoDoc, setSubiendoDoc] = useState(null)
  const [saldo, setSaldo] = useState(null) // { dias_anuales, dias_consumidos, dias_pendientes, dias_restantes }
  const [vista, setVista] = useState('lista') // 'lista' | 'calendario'

  useEffect(() => {
    cargarAusencias()
    cargarResumen()
    cargarSaldo()
  }, [anioSeleccionado, filtroEstado]) // eslint-disable-line react-hooks/exhaustive-deps

  const cargarSaldo = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(
        `${API_URL}/ausencias/mi-saldo?anio=${anioSeleccionado}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (response.ok) setSaldo(await response.json())
    } catch (error) {
      console.error('Error al cargar saldo:', error)
    }
  }

  const cargarAusencias = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      let url = `${API_URL}/ausencias/mis-ausencias?anio=${anioSeleccionado}`
      if (filtroEstado !== 'todas') url += `&estado=${filtroEstado}`
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      if (response.ok) setAusencias(await response.json())
    } catch {
      toast.error('Error al cargar ausencias')
    } finally {
      setLoading(false)
    }
  }

  const cargarResumen = async () => {
    try {
      const token = localStorage.getItem('token')
      const empleadoId = JSON.parse(atob(token.split('.')[1])).empleadoId
      const response = await fetch(
        `${API_URL}/ausencias/resumen/${empleadoId}/${anioSeleccionado}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (response.ok) setResumen(await response.json())
    } catch (error) {
      console.error('Error al cargar resumen:', error)
    }
  }

  const handleCancelarAusencia = async (id) => {
    if (!window.confirm('¿Estás seguro de cancelar esta solicitud?')) return
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/ausencias/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        toast.success('Solicitud cancelada')
        cargarAusencias()
        cargarResumen()
        cargarSaldo()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al cancelar')
      }
    } catch {
      toast.error('Error al cancelar solicitud')
    }
  }

  // B6 — Solicitar cancelación de una ausencia aprobada (la confirma el admin).
  const solicitarCancelacion = async (id) => {
    const motivo = window.prompt('Motivo de la cancelación (opcional):', '')
    if (motivo === null) return // canceló el prompt
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/ausencias/${id}/solicitar-cancelacion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo })
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success('Cancelación solicitada. Pendiente de que el administrador la confirme.')
      cargarAusencias()
    } catch (error) {
      toast.error(error.message)
    }
  }

  // B6 — Responder a una cancelación que solicitó el admin (confirmar/rechazar).
  const responderCancelacion = async (id, accion) => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/ausencias/${id}/responder-cancelacion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accion })
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success(accion === 'confirmar' ? 'Permiso cancelado' : 'Cancelación rechazada; el permiso sigue vigente')
      cargarAusencias()
      cargarResumen()
      cargarSaldo()
    } catch (error) {
      toast.error(error.message)
    }
  }

  const adjuntarDocumento = async (ausenciaId, file) => {
    setSubiendoDoc(ausenciaId)
    try {
      const token = localStorage.getItem('token')
      const fd = new FormData()
      fd.append('documento', file)
      const r = await fetch(`${API_URL}/ausencias/${ausenciaId}/adjuntar-documento`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Error')
      toast.success('Documento adjuntado correctamente')
      cargarAusencias()
    } catch (error) {
      toast.error(error.message)
    } finally {
      setSubiendoDoc(null)
    }
  }

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  return (
    <>
      <PageHeader
        num="§ A1"
        kicker="Mi calendario"
        title="Mis permisos y vacaciones."
        italic="Solicitudes y saldo."
        sub={`Año ${anioSeleccionado} · ${ausencias.length} ${ausencias.length === 1 ? 'solicitud' : 'solicitudes'}`}
        actions={
          <Btn
            variant="primary"
            size="sm"
            onClick={() => setModalAusencia({ open: true, ausencia: null })}
          >
            <FiPlus size={14} /> Nueva solicitud
          </Btn>
        }
      />

      <div className="px-7 py-6 space-y-6">
        {/* Saldo de vacaciones (Punto 4.A) */}
        {saldo && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Días anuales" value={saldo.dias_anuales} unit="días" sub="Asignados por la empresa" />
            <Stat label="Consumidos" value={saldo.dias_consumidos} unit="días" sub="Vacaciones/días libres aprobados" />
            <Stat label="Pendientes" value={saldo.dias_pendientes} unit="días" sub="A la espera de aprobación" />
            <Stat
              label="Restantes"
              value={saldo.dias_restantes}
              unit="días"
              sub={saldo.dias_restantes < 0 ? '⚠️ Saldo excedido' : 'Disponibles este año'}
            />
          </div>
        )}

        {/* Pestañas Lista | Calendario (Punto 4.B) */}
        <Tabs
          items={[{ value: 'lista', label: 'Lista' }, { value: 'calendario', label: 'Calendario' }]}
          value={vista}
          onChange={setVista}
        />

        {vista === 'calendario' && (
          <MiCalendarioMes onChange={() => { cargarAusencias(); cargarResumen(); cargarSaldo() }} />
        )}

        {vista === 'lista' && (<>
        {/* Resumen por tipo */}
        {resumen.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {resumen.map((item) => (
              <Stat
                key={item.tipo}
                label={TIPO_LABEL[item.tipo] || item.tipo}
                value={item.dias_totales}
                unit="días"
                sub={`${item.cantidad} solicitud${item.cantidad !== 1 ? 'es' : ''}`}
              />
            ))}
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            icon={FiCalendar}
            label="Año"
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
          >
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </FilterSelect>
          <FilterSelect
            label="Estado"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="todas">Todas</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobada">Aprobadas</option>
            <option value="rechazada">Rechazadas</option>
          </FilterSelect>
        </div>

        {/* Tabla */}
        {loading ? (
          <Card eyebrow="Detalle" title="Cargando…">
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-soft bg-[var(--bg-2)] animate-pulse" />
              ))}
            </div>
          </Card>
        ) : ausencias.length === 0 ? (
          <Card>
            <Empty
              title="Sin ausencias."
              sub="No se encontraron ausencias para el período seleccionado."
              action={
                <Btn variant="primary" size="sm" onClick={() => setModalAusencia({ open: true, ausencia: null })}>
                  <FiPlus size={14} /> Nueva solicitud
                </Btn>
              }
            />
          </Card>
        ) : (
          <Card
            eyebrow="Detalle"
            title={`Ausencias — ${anioSeleccionado}`}
            action={<span className="text-[11.5px] text-[var(--ink-3)] tnum">{ausencias.length} {ausencias.length === 1 ? 'registro' : 'registros'}</span>}
            bodyClassName="p-0"
          >
            <div className="overflow-x-auto">
              <div className="grid grid-cols-12 px-5 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] bg-[var(--bg-2)]/50 border-b border-[var(--line)] min-w-[820px]">
                <div className="col-span-2">Tipo</div>
                <div className="col-span-3">Fechas</div>
                <div className="col-span-1 text-right">Días</div>
                <div className="col-span-2">Estado</div>
                <div className="col-span-3">Motivo</div>
                <div className="col-span-1 text-right">Acción</div>
              </div>
              <div className="divide-y divide-[var(--line)] min-w-[820px]">
                {ausencias.map((a) => (
                  <div key={a.id} className="grid grid-cols-12 items-start px-5 py-3.5 text-[12.5px] hover:bg-[var(--bg-2)]/40 transition-colors">
                    <div className="col-span-2">
                      <Pill tone={TIPO_TONE[a.tipo] || 'neutral'}>
                        {TIPO_LABEL[a.tipo] || a.tipo}
                      </Pill>
                    </div>
                    <div className="col-span-3 flex items-center gap-2 text-[var(--ink-2)]">
                      <FiCalendar size={13} className="text-[var(--ink-3)]" />
                      <span className="font-mono tnum">
                        {formatearFecha(a.fecha_inicio)}
                        {a.fecha_inicio !== a.fecha_fin && (<> – {formatearFecha(a.fecha_fin)}</>)}
                      </span>
                    </div>
                    <div className="col-span-1 text-right font-mono tnum font-medium">
                      {a.dias_laborables}
                    </div>
                    <div className="col-span-2">
                      <Pill tone={ESTADO_TONE[a.estado] || 'neutral'}>
                        {a.estado === 'pendiente' && <FiClock size={11} />}
                        {a.estado === 'aprobada' && <FiCheckCircle size={11} />}
                        {a.estado === 'rechazada' && <FiXCircle size={11} />}
                        {a.estado === 'documentacion_requerida' && <FiAlertCircle size={11} />}
                        {a.estado === 'cancelada' && <FiXCircle size={11} />}
                        {ESTADO_LABEL[a.estado] || a.estado}
                      </Pill>
                      {/* B6 — Cancelación: solicitud del admin a confirmar, o pendiente de admin */}
                      {a.cancelacion_estado === 'solicitada' && a.cancelacion_solicitada_por_rol === 'admin' && (
                        <div className="mt-2 p-2 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.2)]">
                          <p className="text-[11px] text-[#A86A06] mb-1.5">
                            El administrador propone cancelar este permiso.
                            {a.cancelacion_motivo ? <span className="italic"> «{a.cancelacion_motivo}»</span> : null}
                          </p>
                          <div className="flex gap-1.5">
                            <button
                              type="button"
                              onClick={() => responderCancelacion(a.id, 'confirmar')}
                              className="text-[11px] px-2 py-1 rounded bg-[#C13030] text-white hover:opacity-90 transition-opacity"
                            >
                              Confirmar cancelación
                            </button>
                            <button
                              type="button"
                              onClick={() => responderCancelacion(a.id, 'rechazar')}
                              className="text-[11px] px-2 py-1 rounded border border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--bg-2)] transition-colors"
                            >
                              Rechazar
                            </button>
                          </div>
                        </div>
                      )}
                      {a.cancelacion_estado === 'solicitada' && a.cancelacion_solicitada_por_rol === 'empleado' && (
                        <div className="mt-2 text-[11px] text-[var(--ink-3)] italic">
                          Cancelación solicitada · pendiente de que el administrador la confirme
                        </div>
                      )}
                      {a.estado === 'documentacion_requerida' && (
                        <div className="mt-2">
                          <p className="text-[11px] text-[#A86A06] mb-1">{a.documentacion_solicitada || 'Se requiere documentación'}</p>
                          <SubidaJustificante
                            ausenciaId={a.id}
                            subiendo={subiendoDoc === a.id}
                            onArchivo={adjuntarDocumento}
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-span-3 min-w-0">
                      <div className="text-[var(--ink-2)] truncate">
                        {a.motivo || <span className="text-[var(--ink-3)]">—</span>}
                      </div>
                      {a.comentario_aprobacion && (
                        <div className="text-[11px] text-[var(--ink-3)] mt-0.5 truncate">
                          Comentario: {a.comentario_aprobacion}
                        </div>
                      )}
                      {a.documento_justificativo ? (
                        <div className="text-[11px] text-[#0F8C66] mt-0.5 inline-flex items-center gap-1">
                          <FiPaperclip size={10} /> Documento adjunto
                        </div>
                      ) : a.tipo_requiere_documento && !['rechazada', 'documentacion_requerida'].includes(a.estado) ? (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-[11px] text-[#A86A06]">Justificante pendiente</span>
                          <SubidaJustificante
                            ausenciaId={a.id}
                            subiendo={subiendoDoc === a.id}
                            onArchivo={adjuntarDocumento}
                          />
                        </div>
                      ) : a.tipo_requiere_documento ? (
                        <div className="text-[11px] text-[#A86A06] mt-0.5">
                          Justificante pendiente
                        </div>
                      ) : null}
                    </div>
                    <div className="col-span-1 text-right">
                      {a.estado === 'pendiente' && (
                        <button
                          type="button"
                          onClick={() => handleCancelarAusencia(a.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[#C13030] hover:bg-[rgba(239,68,68,.06)] transition-colors"
                          title="Cancelar solicitud"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                      {/* B6 — Solicitar cancelación de una aprobada (sin cancelación en curso) */}
                      {a.estado === 'aprobada' && a.cancelacion_estado !== 'solicitada' && (
                        <button
                          type="button"
                          onClick={() => solicitarCancelacion(a.id)}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[#C13030] hover:bg-[rgba(239,68,68,.06)] transition-colors"
                          title="Solicitar cancelación"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
        </>)}
      </div>

      {modalAusencia.open && (
        <SolicitudAusenciaModal
          ausencia={modalAusencia.ausencia}
          onClose={() => setModalAusencia({ open: false, ausencia: null })}
          onSuccess={() => {
            setModalAusencia({ open: false, ausencia: null })
            cargarAusencias()
            cargarResumen()
            cargarSaldo()
          }}
        />
      )}
    </>
  )
}

export default MisAusenciasV2
